export const IVR_END_ELEMENT_RESERVED_ID = 999;
export const IVR_NODE_WIDTH = 120;
export const IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT = 50;
export const IVR_TREE_TRUNK_TO_BUS_GAP = 28;
export const IVR_TREE_BUS_TO_CHILD_DROP_PX = 36;
export const IVR_TREE_PARENT_TO_CHILD_GAP = 80;
export const IVR_NODE_SIBLING_GAP_PX = 80;
export const IVR_TIDY_SUBTREE_GAP_PX = 80;
export const IVR_SCHEDULER_TRUNK_TO_BUS_GAP = IVR_TREE_TRUNK_TO_BUS_GAP;
export const IVR_SCHEDULER_TO_CHILD_VERTICAL_GAP = IVR_TREE_PARENT_TO_CHILD_GAP;
export const IVR_SCHEDULER_SIBLING_GAP_PX = 200;
export function ivrSchedulerSlotRowY(
    configTopY: number,
    configRootHeight: number = IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT
): number {
    return (
        configTopY +
        configRootHeight +
        IVR_TREE_TRUNK_TO_BUS_GAP +
        IVR_TREE_BUS_TO_CHILD_DROP_PX
    );
}

export function ivrSchedulerDayFirstSlotY(
    dayTopY: number,
    dayHeight: number = IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT
): number {
    return (
        dayTopY +
        dayHeight +
        IVR_TREE_TRUNK_TO_BUS_GAP +
        IVR_TREE_BUS_TO_CHILD_DROP_PX
    );
}

export function ivrSchedulerDaySlotRowLayout(
    dayTopY: number,
    columnCenterX: number,
    slotIndex: number,
    slotCount: number,
    dayHeight: number = IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT
): { x: number; y: number } {
    const y = ivrSchedulerDayFirstSlotY(dayTopY, dayHeight);
    const gap = IVR_SCHEDULER_SIBLING_GAP_PX;
    const totalSpan = Math.max(0, slotCount - 1) * gap;
    const startCenterX = columnCenterX - totalSpan / 2;
    return {
        x: startCenterX + slotIndex * gap - IVR_NODE_WIDTH / 2,
        y,
    };
}

export function ivrSchedulerAdvancedColumnWidth(slotCount: number): number {
    return Math.max(
        IVR_SCHEDULER_SIBLING_GAP_PX,
        Math.max(1, slotCount) * IVR_SCHEDULER_SIBLING_GAP_PX
    );
}

export const IVR_START_ABOVE_ENTRY_GAP = 72;
export const IVR_MENU_DTMF_GAP_PX = IVR_NODE_WIDTH + IVR_NODE_SIBLING_GAP_PX;
export const IVR_PLAY_CHAIN_VERTICAL_GAP = 100;
export const IVR_MENU_DTMF_VERTICAL_GAP = 100;

export type IvrMenuRetriesBranchLayout = {
    retries: { x: number; y: number };
    trueNode: { x: number; y: number };
    falseNode: { x: number; y: number };
};

export function ivrMenuRetriesBranchLayout(
    dtmfKeyX: number,
    dtmfChildY: number
): IvrMenuRetriesBranchLayout {
    const retriesY = dtmfChildY + IVR_MENU_DTMF_VERTICAL_GAP;
    const branchY = retriesY + IVR_MENU_DTMF_VERTICAL_GAP;
    const halfSpread = (IVR_NODE_WIDTH + IVR_NODE_SIBLING_GAP_PX) / 2;
    return {
        retries: { x: dtmfKeyX, y: retriesY },
        trueNode: { x: dtmfKeyX - halfSpread, y: branchY },
        falseNode: { x: dtmfKeyX + halfSpread, y: branchY },
    };
}

export function ivrEstimateMenuChainHeight(
    config: Record<string, unknown>
): number {
    const menuMeta =
        (config.menuAudioMeta as { audioId: number; audioName: string }[]) ?? [];
    const menuSilensPre = config.menuSilensPre as number | null | undefined;
    const menuSilensPost = config.menuSilensPost as number | null | undefined;
    const menuRetries = config.menuRetries as number | null | undefined;
    const menuTimeoutRetries = config.menuTimeoutRetries as
        | number
        | null
        | undefined;
    const menuDtmfRetries =
        (config.menuDtmfRetries as
            | Record<string, number | null | undefined>
            | undefined) ?? {};

    let y = IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT;
    if (menuSilensPre != null) y += IVR_PLAY_CHAIN_VERTICAL_GAP;
    y += menuMeta.length * IVR_PLAY_CHAIN_VERTICAL_GAP;
    if (menuSilensPost != null) y += IVR_PLAY_CHAIN_VERTICAL_GAP;
    y += IVR_MENU_DTMF_VERTICAL_GAP;
    y += IVR_MENU_DTMF_VERTICAL_GAP;

    const hasRetries =
        Object.values(menuDtmfRetries).some((v) => v != null) ||
        menuTimeoutRetries != null ||
        menuRetries != null;
    if (hasRetries) {
        y += 2 * IVR_MENU_DTMF_VERTICAL_GAP;
    }

    return y + IVR_TREE_PARENT_TO_CHILD_GAP;
}

export const IVR_AGENT_HUNTER_QUEUE_VERTICAL_GAP = 112;
export function ivrAgentHunterQueueChildY(
    configTopY: number,
    configRootHeight: number = IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT
): number {
    return (
        configTopY + configRootHeight + IVR_AGENT_HUNTER_QUEUE_VERTICAL_GAP
    );
}
export const IVR_EDGE_LOCAL_HORIZONTAL_SPAN_PX = 96;

export const IVR_EDGE_CORRIDOR_BELOW_SOURCE_PX = 32;

export const IVR_MENU_DTMF_KEY_ORDER = [
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "*",
    "0",
    "#",
] as const;

export function sortIvrMenuDtmfKeys(
    keys: readonly string[] | null | undefined
): string[] {
    if (!keys?.length) return [];
    const normalized = keys
        .map((k) => String(k).trim())
        .filter((k) => k.length > 0);
    const set = new Set(normalized);
    const order = IVR_MENU_DTMF_KEY_ORDER as readonly string[];
    const out: string[] = [];
    for (const k of order) {
        if (set.has(k)) out.push(k);
    }
    for (const k of normalized) {
        if (!order.includes(k) && !out.includes(k)) out.push(k);
    }
    return out;
}
